import React, { useEffect, useRef } from "react";

import { useLazyQuery } from "@apollo/client";
import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";

import { ACCOUNT_QUERIES } from "components/graphql";
import { useTranslation } from "components/i18n/source";
import { Button, Form, Input, Text } from "components/ui/source";

import OAuthContainer from "./OAuthContainer";
import { useFlow } from "./utils";
import { validateEmailSelectorForm } from "./validator";

import styles from "./Accounts.module.scss";

function SignUpSelectorContainer({
  setLoading,
  setEmail,
  showLogin,
  logSubmit,
}) {
  const { t } = useTranslation();
  const flow = useFlow();
  const emailFormRef = useRef(null);

  const prefix = "modals.accounts.signUp.selector";

  const [checkEmail, { data, error, variables }] = useLazyQuery(
    ACCOUNT_QUERIES.EMAIL_HAS_BEEN_TAKEN,
  );

  useEffect(() => {
    setLoading(false);

    // Switch to login mode if email has been already taken
    if (data?.emailHasBeenTaken?.used) {
      showLogin(variables?.email);
    } else if (data || error) {
      setEmail(variables?.email);
    }
  }, [data, error]);

  function onSubmitEmail(data) {
    setLoading(true);
    checkEmail({
      variables: {
        email: data.email,
      },
    });
  }

  return (
    <>
      <Text
        className={classnames(styles.header, styles.centered_text)}
        types={["h4", "medium"]}
      >
        {flow.signUpSelectorTitle}
      </Text>

      <Form
        className={styles.form_container}
        validate={values => validateEmailSelectorForm(values, t)}
        initialValues={{}}
        initialErrors={{ email: "" }}
        onSubmit={onSubmitEmail}
        innerRef={emailFormRef}
        validateOnChange={true}
      >
        {({ handleSubmit, errors }) => (
          <>
            <Input
              placeholder={t("modals.accounts.email.email")}
              type="email"
              name="email"
              theme="popup"
              required
            />
            <Button
              htmlType="submit"
              textProps={{
                translateKey: `${prefix}.cta`,
                types: ["subtitle"],
              }}
              upperCase={false}
              onClick={handleSubmit}
              disabled={!isEmpty(errors)}
            />
          </>
        )}
      </Form>

      {/* Disable oAuth on Referral flow */}
      {flow.showOAuth && (
        <>
          <Text
            className={styles.centered_text}
            types={["subtitles"]}
            translateKey={`${prefix}.separator`}
          />
          <OAuthContainer
            setLoading={setLoading}
            logSubmit={logSubmit}
            theme="signup"
          />
        </>
      )}
    </>
  );
}

SignUpSelectorContainer.propTypes = {
  setLoading: PropTypes.func,
  setEmail: PropTypes.func,
  showLogin: PropTypes.func,
  logSubmit: PropTypes.func.isRequired,
};

SignUpSelectorContainer.defaultProps = {
  setLoading: () => {},
  setEmail: () => {},
  showLogin: () => {},
  logSubmit: async () => {},
};

export default SignUpSelectorContainer;
